import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type { BlogImageData, BlogPostData, SiteData } from '@/types/api';

/**
 * Blog authoring API. Sites and posts are scoped server-side by
 * Site::isManageableBy — an admin manages any site, a portal client only their
 * own. The same hooks serve both surfaces.
 */

type PostFilters = {
  status?: string;
  q?: string;
  page?: number;
};

const POSTS_KEY = (siteId: number) => ['blog', 'sites', siteId, 'posts'];
const POST_KEY = (siteId: number, postId: number) => ['blog', 'sites', siteId, 'posts', postId];

export function useMySites() {
  return useQuery({
    queryKey: ['blog', 'sites'],
    queryFn: async (): Promise<Paginated<SiteData>> => {
      const { data } = await api.get<Paginated<SiteData>>('/v1/blog/sites');
      return data;
    },
  });
}

export function usePosts(siteId: number | undefined, filters: PostFilters = {}) {
  return useQuery({
    queryKey: [...(siteId ? POSTS_KEY(siteId) : ['blog', 'sites', 'none', 'posts']), filters],
    enabled: siteId != null,
    queryFn: async (): Promise<Paginated<BlogPostData>> => {
      const { data } = await api.get<Paginated<BlogPostData>>(`/v1/sites/${siteId}/posts`, {
        params: filters,
      });
      return data;
    },
  });
}

export function usePost(siteId: number | undefined, postId: number | undefined) {
  return useQuery({
    queryKey: siteId && postId ? POST_KEY(siteId, postId) : ['blog', 'post', 'none'],
    enabled: siteId != null && postId != null,
    queryFn: async (): Promise<BlogPostData> => {
      const { data } = await api.get<BlogPostData>(`/v1/sites/${siteId}/posts/${postId}`);
      return data;
    },
  });
}

export function useCreatePost(siteId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<BlogPostData>): Promise<BlogPostData> => {
      await primeCsrf();
      const { data } = await api.post<BlogPostData>(`/v1/sites/${siteId}/posts`, input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: POSTS_KEY(siteId) }),
  });
}

export function useUpdatePost(siteId: number, postId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<BlogPostData>): Promise<BlogPostData> => {
      await primeCsrf();
      const { data } = await api.patch<BlogPostData>(`/v1/sites/${siteId}/posts/${postId}`, patch);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: POSTS_KEY(siteId) });
      qc.invalidateQueries({ queryKey: POST_KEY(siteId, postId) });
    },
  });
}

export function useDeletePost(siteId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (postId: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/sites/${siteId}/posts/${postId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: POSTS_KEY(siteId) }),
  });
}

type UploadInput = { file: File; collection: 'featured' | 'images' };

export function useUploadImage(siteId: number, postId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      file,
      collection,
    }: UploadInput): Promise<{ image: BlogImageData; post: BlogPostData }> => {
      await primeCsrf();
      const body = new FormData();
      body.append('image', file);
      body.append('collection', collection);
      const { data } = await api.post<{ image: BlogImageData; post: BlogPostData }>(
        `/v1/sites/${siteId}/posts/${postId}/images`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: POSTS_KEY(siteId) });
      qc.invalidateQueries({ queryKey: POST_KEY(siteId, postId) });
    },
  });
}

export function useDeleteImage(siteId: number, postId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mediaId: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/sites/${siteId}/posts/${postId}/images/${mediaId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: POST_KEY(siteId, postId) });
    },
  });
}

/** Client action — marks a post ready and notifies Mehrutherm. Does not publish. */
export function useSubmitPost(siteId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (postId: number): Promise<BlogPostData> => {
      await primeCsrf();
      const { data } = await api.post<BlogPostData>(`/v1/sites/${siteId}/posts/${postId}/submit`);
      return data;
    },
    onSuccess: (_, postId) => {
      qc.invalidateQueries({ queryKey: POSTS_KEY(siteId) });
      qc.invalidateQueries({ queryKey: POST_KEY(siteId, postId) });
    },
  });
}

export function useRevertPost(siteId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (postId: number): Promise<BlogPostData> => {
      await primeCsrf();
      const { data } = await api.post<BlogPostData>(`/v1/sites/${siteId}/posts/${postId}/revert`);
      return data;
    },
    onSuccess: (_, postId) => {
      qc.invalidateQueries({ queryKey: POSTS_KEY(siteId) });
      qc.invalidateQueries({ queryKey: POST_KEY(siteId, postId) });
    },
  });
}

/** Admin-only — freezes the live snapshot the public API serves. */
export function useGoLive(siteId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (postId: number): Promise<BlogPostData> => {
      await primeCsrf();
      const { data } = await api.post<BlogPostData>(`/v1/sites/${siteId}/posts/${postId}/go-live`);
      return data;
    },
    onSuccess: (_, postId) => {
      qc.invalidateQueries({ queryKey: POSTS_KEY(siteId) });
      qc.invalidateQueries({ queryKey: POST_KEY(siteId, postId) });
    },
  });
}

export function useUnpublish(siteId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (postId: number): Promise<BlogPostData> => {
      await primeCsrf();
      const { data } = await api.post<BlogPostData>(`/v1/sites/${siteId}/posts/${postId}/unpublish`);
      return data;
    },
    onSuccess: (_, postId) => {
      qc.invalidateQueries({ queryKey: POSTS_KEY(siteId) });
      qc.invalidateQueries({ queryKey: POST_KEY(siteId, postId) });
    },
  });
}
